import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function InsuranceTracking() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [activeTab, setActiveTab] = useState('policies');

  // Data
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [dues, setDues] = useState([]);
  const [pendingActions, setPendingActions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [employees, setEmployees] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterResponsibility, setFilterResponsibility] = useState('');

  // Modals
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);

  // Form
  const [form, setForm] = useState({ employee_id: '', type: 'employee', relative_name: '', relationship: '', provider: '', start_date: '', expiry_date: '', annual_price: '', payment_schedule: 'monthly', payment_responsibility: 'company', notes: '' });
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().split('T')[0], amount: '', insurance_due_id: '', notes: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const authCheck = useCallback((res) => {
    if (res.status === 401 || res.status === 403) { navigate('/login'); throw new Error('Unauthorized'); }
    return res;
  }, [navigate]);

  // ─── FETCH ───
  const fetchPolicies = useCallback(async () => {
    try {
      let url = `${API}/companies/${companyId}/insurance?`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterType) url += `&type=${filterType}`;
      if (filterResponsibility) url += `&payment_responsibility=${filterResponsibility}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setPolicies(data.data || []);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to load policies'); }
  }, [companyId, filterStatus, filterType, filterResponsibility, authCheck]);

  const fetchDues = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/dues`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setDues(data.data || []);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/pending-actions`, { headers: getAuthHeaders() });
      authCheck(res);
      setPendingActions(await res.json());
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/audit-history`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setAuditLogs(data.data || []);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/employees?status=Active&per_page=500`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      setEmployees(data.data || data || []);
    } catch (e) { if (e.message !== 'Unauthorized') console.error(e); }
  }, [companyId, authCheck]);

  useEffect(() => {
    if (!companyId) return;
    fetchPolicies();
    fetchEmployees();
    if (activeTab === 'dues') fetchDues();
    if (activeTab === 'pending') fetchPending();
    if (activeTab === 'audit') fetchAudit();
  }, [companyId, activeTab, fetchPolicies, fetchEmployees, fetchDues, fetchPending, fetchAudit]);

  // ─── ACTIONS ───
  const savePolicy = async () => {
    setLoading(true); setError('');
    try {
      const url = editingPolicy
        ? `${API}/companies/${companyId}/insurance/${editingPolicy.id}`
        : `${API}/companies/${companyId}/insurance`;
      const method = editingPolicy ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      authCheck(res);
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSuccess(editingPolicy ? 'Policy updated' : 'Policy created');
      setShowPolicyModal(false);
      resetForm();
      fetchPolicies();
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to save'); }
    finally { setLoading(false); }
  };

  const archivePolicy = async (id) => {
    if (!window.confirm('Archive this policy?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/${id}/archive`, { method: 'POST', headers: getAuthHeaders() });
      authCheck(res);
      if (res.ok) { setSuccess('Policy archived'); fetchPolicies(); }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed'); }
  };

  const recordPayment = async () => {
    if (!selectedPolicy) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/${selectedPolicy.id}/payments`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payForm, amount: parseFloat(payForm.amount), insurance_due_id: payForm.insurance_due_id || null }),
      });
      authCheck(res);
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setSelectedPolicy(data);
      setShowPaymentModal(false);
      setPayForm({ payment_date: new Date().toISOString().split('T')[0], amount: '', insurance_due_id: '', notes: '' });
      setSuccess('Payment recorded');
      fetchPolicies();
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed'); }
    finally { setLoading(false); }
  };

  const deletePayment = async (paymentId) => {
    if (!selectedPolicy || !window.confirm('Delete this payment?')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/${selectedPolicy.id}/payments/${paymentId}`, { method: 'DELETE', headers: getAuthHeaders() });
      authCheck(res);
      if (res.ok) {
        const data = await res.json();
        setSelectedPolicy(data);
        fetchPolicies();
      }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed'); }
  };

  const openDetail = async (policy) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/${policy.id}`, { headers: getAuthHeaders() });
      authCheck(res);
      setSelectedPolicy(await res.json());
      setShowDetailModal(true);
    } catch (e) { if (e.message !== 'Unauthorized') setError('Failed to load'); }
  };

  const openEdit = (policy) => {
    setEditingPolicy(policy);
    setForm({ employee_id: policy.employee_id, type: policy.type, relative_name: policy.relative_name || '', relationship: policy.relationship || '', provider: policy.provider, start_date: policy.start_date?.split('T')[0], expiry_date: policy.expiry_date?.split('T')[0], annual_price: policy.annual_price, payment_schedule: policy.payment_schedule, payment_responsibility: policy.payment_responsibility, notes: policy.notes || '' });
    setShowPolicyModal(true);
  };

  const resetForm = () => {
    setEditingPolicy(null);
    setForm({ employee_id: '', type: 'employee', relative_name: '', relationship: '', provider: '', start_date: '', expiry_date: '', annual_price: '', payment_schedule: 'monthly', payment_responsibility: 'company', notes: '' });
  };

  const exportData = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/insurance/export`, { headers: getAuthHeaders() });
      authCheck(res);
      const data = await res.json();
      if (data.rows && data.rows.length > 0) {
        const headers = Object.keys(data.rows[0]);
        const csv = [headers.join(','), ...data.rows.map(r => headers.map(h => `"${r[h] ?? ''}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'insurance_export.csv'; a.click();
      }
    } catch (e) { if (e.message !== 'Unauthorized') setError('Export failed'); }
  };

  // ─── STYLES ───
  const containerStyle = { padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: '1400px', margin: '0 auto' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' };
  const titleStyle = { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 };
  const btnStyle = (color = '#3b82f6') => ({ padding: '8px 16px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' });
  const btnSmStyle = (color = '#3b82f6') => ({ padding: '4px 10px', backgroundColor: color, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });
  const tabStyle = (active) => ({ padding: '10px 20px', border: 'none', borderBottom: active ? '3px solid #3b82f6' : '3px solid transparent', background: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: active ? '600' : '400', color: active ? '#3b82f6' : '#64748b' });
  const cardStyle = { background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' };
  const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px' };
  const thStyle = { padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', fontWeight: '600', color: '#475569', fontSize: '12px', textTransform: 'uppercase' };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
  const modalOverlay = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalContent = { backgroundColor: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' };
  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };
  const selectStyle = { ...inputStyle };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' };
  const statusBadge = (status) => {
    const colors = { active: '#10b981', expiring_soon: '#f59e0b', expired: '#ef4444', archived: '#6b7280', upcoming: '#3b82f6', due: '#f59e0b', overdue: '#ef4444', paid: '#10b981' };
    return { display: 'inline-block', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: `${colors[status] || '#94a3b8'}20`, color: colors[status] || '#94a3b8' };
  };
  const alertStyle = (type) => ({ padding: '12px 16px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', backgroundColor: type === 'error' ? '#fef2f2' : '#f0fdf4', color: type === 'error' ? '#dc2626' : '#16a34a', border: `1px solid ${type === 'error' ? '#fecaca' : '#bbf7d0'}` });
  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h1 style={titleStyle}>Insurance Tracking</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={btnStyle('#0ea5e9')} onClick={exportData}>Export</button>
          <button style={btnStyle('#3b82f6')} onClick={() => { resetForm(); setShowPolicyModal(true); }}>+ New Policy</button>
        </div>
      </div>

      {error && <div style={alertStyle('error')}>{error} <span style={{ cursor: 'pointer', float: 'right' }} onClick={() => setError('')}>✕</span></div>}
      {success && <div style={alertStyle('success')}>{success} <span style={{ cursor: 'pointer', float: 'right' }} onClick={() => setSuccess('')}>✕</span></div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e2e8f0', marginBottom: '24px', overflowX: 'auto' }}>
        {['policies', 'dues', 'pending', 'audit'].map(t => (
          <button key={t} style={tabStyle(activeTab === t)} onClick={() => setActiveTab(t)}>
            {t === 'policies' ? 'Policies' : t === 'dues' ? 'Dues' : t === 'pending' ? 'Pending Actions' : 'Audit History'}
          </button>
        ))}
      </div>

      {/* ═══ POLICIES TAB ═══ */}
      {activeTab === 'policies' && (
        <div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <select style={{ ...selectStyle, width: '140px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="expiring_soon">Expiring Soon</option>
              <option value="expired">Expired</option>
              <option value="archived">Archived</option>
            </select>
            <select style={{ ...selectStyle, width: '130px' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option value="employee">Employee</option>
              <option value="relative">Relative</option>
            </select>
            <select style={{ ...selectStyle, width: '160px' }} value={filterResponsibility} onChange={e => setFilterResponsibility(e.target.value)}>
              <option value="">All Responsibility</option>
              <option value="company">Company Paid</option>
              <option value="employee_deduction">Employee Deduction</option>
            </select>
          </div>

          <div style={{ ...cardStyle, overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Employee</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Provider</th>
                  <th style={thStyle}>Period</th>
                  <th style={thStyle}>Annual Price</th>
                  <th style={thStyle}>Paid</th>
                  <th style={thStyle}>Remaining</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 && <tr><td colSpan="9" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No policies found</td></tr>}
                {policies.map(p => (
                  <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(p)}>
                    <td style={tdStyle}>
                      <strong>{p.employee?.name || '—'}</strong>
                      {p.type === 'relative' && <div style={{ fontSize: '11px', color: '#94a3b8' }}>{p.relative_name} ({p.relationship})</div>}
                    </td>
                    <td style={tdStyle}><span style={{ textTransform: 'capitalize' }}>{p.type}</span></td>
                    <td style={tdStyle}>{p.provider}</td>
                    <td style={tdStyle}><span style={{ fontSize: '12px' }}>{p.start_date?.split('T')[0]} → {p.expiry_date?.split('T')[0]}</span></td>
                    <td style={tdStyle}>${fmt(p.annual_price)}</td>
                    <td style={{ ...tdStyle, color: '#16a34a' }}>${fmt(p.total_paid)}</td>
                    <td style={{ ...tdStyle, color: p.remaining_balance > 0 ? '#f59e0b' : '#10b981' }}>${fmt(p.remaining_balance)}</td>
                    <td style={tdStyle}><span style={statusBadge(p.status)}>{p.status.replace('_', ' ')}</span></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button style={btnSmStyle('#8b5cf6')} onClick={() => openEdit(p)}>Edit</button>
                        {p.status !== 'archived' && <button style={btnSmStyle('#6b7280')} onClick={() => archivePolicy(p.id)}>Archive</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ DUES TAB ═══ */}
      {activeTab === 'dues' && (
        <div style={{ ...cardStyle, overflowX: 'auto' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Insurance Dues</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Employee</th>
                <th style={thStyle}>Provider</th>
                <th style={thStyle}>Due Date</th>
                <th style={thStyle}>Scheduled</th>
                <th style={thStyle}>Paid</th>
                <th style={thStyle}>Remaining</th>
                <th style={thStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {dues.length === 0 && <tr><td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No dues</td></tr>}
              {dues.map(d => (
                <tr key={d.id}>
                  <td style={tdStyle}>{d.policy?.employee?.name || '—'}</td>
                  <td style={tdStyle}>{d.policy?.provider || '—'}</td>
                  <td style={tdStyle}>{d.due_date?.split('T')[0]}</td>
                  <td style={tdStyle}>${fmt(d.scheduled_amount)}</td>
                  <td style={{ ...tdStyle, color: '#16a34a' }}>${fmt(d.paid_amount)}</td>
                  <td style={{ ...tdStyle, color: d.remaining_amount > 0 ? '#f59e0b' : '#10b981' }}>${fmt(d.remaining_amount)}</td>
                  <td style={tdStyle}><span style={statusBadge(d.status)}>{d.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ PENDING TAB ═══ */}
      {activeTab === 'pending' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Pending Actions ({pendingActions.length})</h3>
          {pendingActions.length === 0 && <p style={{ color: '#94a3b8' }}>No pending actions</p>}
          {pendingActions.map((a, i) => (
            <div key={i} style={{ padding: '12px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ ...statusBadge(a.type === 'expired' ? 'expired' : a.type === 'overdue_due' ? 'overdue' : a.type === 'expiring' ? 'expiring_soon' : 'active'), marginRight: '8px' }}>{a.type.replace('_', ' ')}</span>
                {a.message}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ AUDIT TAB ═══ */}
      {activeTab === 'audit' && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Audit History</h3>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Date</th><th style={thStyle}>User</th><th style={thStyle}>Action</th><th style={thStyle}>Description</th></tr></thead>
            <tbody>
              {auditLogs.length === 0 && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No logs</td></tr>}
              {auditLogs.map(log => (
                <tr key={log.id}><td style={tdStyle}>{new Date(log.created_at).toLocaleString()}</td><td style={tdStyle}>{log.user?.name || '—'}</td><td style={tdStyle}>{log.action}</td><td style={tdStyle}>{log.description}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ POLICY MODAL ═══ */}
      {showPolicyModal && (
        <div style={modalOverlay} onClick={() => setShowPolicyModal(false)}>
          <div style={modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px' }}>{editingPolicy ? 'Edit Policy' : 'New Insurance Policy'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Employee *</label>
                <select style={selectStyle} value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })} disabled={!!editingPolicy}>
                  <option value="">Select employee...</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Policy Type *</label>
                <select style={selectStyle} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} disabled={!!editingPolicy}>
                  <option value="employee">Employee</option>
                  <option value="relative">Relative</option>
                </select>
              </div>
              {form.type === 'relative' && (
                <>
                  <div>
                    <label style={labelStyle}>Relative Name *</label>
                    <input style={inputStyle} value={form.relative_name} onChange={e => setForm({ ...form, relative_name: e.target.value })} />
                  </div>
                  <div>
                    <label style={labelStyle}>Relationship *</label>
                    <select style={selectStyle} value={form.relationship} onChange={e => setForm({ ...form, relationship: e.target.value })}>
                      <option value="">Select...</option>
                      <option value="spouse">Spouse</option>
                      <option value="child">Child</option>
                      <option value="parent">Parent</option>
                      <option value="sibling">Sibling</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </>
              )}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Provider / Policy Name *</label>
                <input style={inputStyle} value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Start Date *</label>
                <input style={inputStyle} type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Expiry Date *</label>
                <input style={inputStyle} type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Annual Price *</label>
                <input style={inputStyle} type="number" step="0.01" value={form.annual_price} onChange={e => setForm({ ...form, annual_price: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Payment Schedule *</label>
                <select style={selectStyle} value={form.payment_schedule} onChange={e => setForm({ ...form, payment_schedule: e.target.value })}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="semi_annual">Semi-Annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Payment Responsibility *</label>
                <select style={selectStyle} value={form.payment_responsibility} onChange={e => setForm({ ...form, payment_responsibility: e.target.value })}>
                  <option value="company">Company Paid</option>
                  <option value="employee_deduction">Employee Payroll Deduction</option>
                </select>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea style={{ ...inputStyle, minHeight: '60px' }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button style={btnStyle('#6b7280')} onClick={() => setShowPolicyModal(false)}>Cancel</button>
              <button style={btnStyle('#3b82f6')} onClick={savePolicy} disabled={loading}>{loading ? 'Saving...' : editingPolicy ? 'Update' : 'Create Policy'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DETAIL MODAL ═══ */}
      {showDetailModal && selectedPolicy && (
        <div style={modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={{ ...modalContent, maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>
                {selectedPolicy.employee?.name} — {selectedPolicy.provider}
                <span style={{ ...statusBadge(selectedPolicy.status), marginLeft: '12px' }}>{selectedPolicy.status?.replace('_', ' ')}</span>
              </h2>
              <button style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }} onClick={() => setShowDetailModal(false)}>✕</button>
            </div>

            {/* Policy info */}
            <div style={{ ...cardStyle, backgroundColor: '#f8fafc' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '13px' }}>
                <div><span style={{ color: '#94a3b8' }}>Type</span><div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{selectedPolicy.type}</div></div>
                {selectedPolicy.type === 'relative' && <div><span style={{ color: '#94a3b8' }}>Relative</span><div style={{ fontWeight: '600' }}>{selectedPolicy.relative_name} ({selectedPolicy.relationship})</div></div>}
                <div><span style={{ color: '#94a3b8' }}>Period</span><div style={{ fontWeight: '600' }}>{selectedPolicy.start_date?.split('T')[0]} → {selectedPolicy.expiry_date?.split('T')[0]}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Annual Price</span><div style={{ fontWeight: '700', fontSize: '16px' }}>${fmt(selectedPolicy.annual_price)}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Total Paid</span><div style={{ fontWeight: '700', fontSize: '16px', color: '#16a34a' }}>${fmt(selectedPolicy.total_paid)}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Remaining</span><div style={{ fontWeight: '700', fontSize: '16px', color: '#f59e0b' }}>${fmt(selectedPolicy.remaining_balance)}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Schedule</span><div style={{ fontWeight: '600' }}>{selectedPolicy.payment_schedule?.replace('_', '-')}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Responsibility</span><div style={{ fontWeight: '600' }}>{selectedPolicy.payment_responsibility === 'company' ? 'Company Paid' : 'Employee Deduction'}</div></div>
                <div><span style={{ color: '#94a3b8' }}>Days Until Expiry</span><div style={{ fontWeight: '600' }}>{selectedPolicy.days_until_expiry}</div></div>
              </div>
            </div>

            {/* Dues */}
            <div style={{ marginTop: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '14px' }}>Payment Schedule</h4>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Due Date</th><th style={thStyle}>Scheduled</th><th style={thStyle}>Paid</th><th style={thStyle}>Remaining</th><th style={thStyle}>Status</th></tr></thead>
                <tbody>
                  {(selectedPolicy.dues || []).map(d => (
                    <tr key={d.id}>
                      <td style={tdStyle}>{d.due_date?.split('T')[0]}</td>
                      <td style={tdStyle}>${fmt(d.scheduled_amount)}</td>
                      <td style={{ ...tdStyle, color: '#16a34a' }}>${fmt(d.paid_amount)}</td>
                      <td style={tdStyle}>${fmt(d.remaining_amount)}</td>
                      <td style={tdStyle}><span style={statusBadge(d.status)}>{d.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Payments */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <h4 style={{ margin: 0, fontSize: '14px' }}>Payment History</h4>
                {selectedPolicy.status !== 'archived' && (
                  <button style={btnSmStyle('#10b981')} onClick={() => setShowPaymentModal(true)}>+ Record Payment</button>
                )}
              </div>
              <table style={tableStyle}>
                <thead><tr><th style={thStyle}>Date</th><th style={thStyle}>Amount</th><th style={thStyle}>Notes</th><th style={thStyle}></th></tr></thead>
                <tbody>
                  {(!selectedPolicy.payments || selectedPolicy.payments.length === 0) && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No payments</td></tr>}
                  {(selectedPolicy.payments || []).map(pay => (
                    <tr key={pay.id}>
                      <td style={tdStyle}>{pay.payment_date?.split('T')[0]}</td>
                      <td style={{ ...tdStyle, fontWeight: '600', color: '#16a34a' }}>${fmt(pay.amount)}</td>
                      <td style={tdStyle}>{pay.notes || '—'}</td>
                      <td style={tdStyle}><button style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }} onClick={() => deletePayment(pay.id)}>✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PAYMENT MODAL ═══ */}
      {showPaymentModal && selectedPolicy && (
        <div style={modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div style={{ ...modalContent, maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px' }}>Record Payment</h2>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>Remaining balance: <strong>${fmt(selectedPolicy.remaining_balance)}</strong></p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={labelStyle}>Payment Date *</label>
                <input style={inputStyle} type="date" value={payForm.payment_date} onChange={e => setPayForm({ ...payForm, payment_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Amount *</label>
                <input style={inputStyle} type="number" step="0.01" value={payForm.amount} onChange={e => setPayForm({ ...payForm, amount: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label style={labelStyle}>Link to Due (optional)</label>
                <select style={selectStyle} value={payForm.insurance_due_id} onChange={e => setPayForm({ ...payForm, insurance_due_id: e.target.value })}>
                  <option value="">No specific due</option>
                  {(selectedPolicy.dues || []).filter(d => d.status !== 'paid').map(d => (
                    <option key={d.id} value={d.id}>{d.due_date?.split('T')[0]} — ${fmt(d.remaining_amount)} remaining</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Notes</label>
                <input style={inputStyle} value={payForm.notes} onChange={e => setPayForm({ ...payForm, notes: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button style={btnStyle('#6b7280')} onClick={() => setShowPaymentModal(false)}>Cancel</button>
              <button style={btnStyle('#10b981')} onClick={recordPayment} disabled={loading || !payForm.amount}>{loading ? 'Recording...' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}